import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { FocusSession, FocusSessionStatus } from '../types/focus';

// --- State Definition ---
interface FocusState {
    currentSession: FocusSession | null;
    isFocusModeActive: boolean;
    timeRemaining: number; // in seconds
    targetEndTime: number | null; // Timestamp when timer successfully ends
    timerDuration: number; // initial duration in minutes
}

const STORAGE_KEY = 'way2will_focus_state';

const loadInitialState = (): FocusState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to load focus state', e);
    }
    return {
        currentSession: null,
        isFocusModeActive: false,
        timeRemaining: 25 * 60,
        targetEndTime: null,
        timerDuration: 25,
    };
};

const initialState: FocusState = loadInitialState();

// --- Action Types ---
type FocusAction =
    | { type: 'START_SESSION'; payload: { taskId?: string; durationMinutes: number } }
    | { type: 'PAUSE_SESSION' }
    | { type: 'RESUME_SESSION' }
    | { type: 'END_SESSION' }
    | { type: 'TICK' }
    | { type: 'SET_DURATION'; payload: number }
    | { type: 'LOG_INTERRUPTION'; payload?: string }
    | { type: 'RESET_SESSION' };

// --- Reducer ---
function focusReducer(state: FocusState, action: FocusAction): FocusState {
    switch (action.type) {
        case 'START_SESSION': {
            const { taskId, durationMinutes } = action.payload;
            const newSession: FocusSession = {
                id: crypto.randomUUID(),
                taskId,
                startTime: new Date().toISOString(),
                duration: durationMinutes,
                actualDurationSeconds: 0,
                interruptionCount: 0,
                interruptionLog: [],
                status: 'active',
            };
            const durationMs = durationMinutes * 60 * 1000;
            const targetEndTime = Date.now() + durationMs;

            return {
                ...state,
                currentSession: newSession,
                isFocusModeActive: true,
                timeRemaining: durationMinutes * 60,
                targetEndTime: targetEndTime,
                timerDuration: durationMinutes,
            };
        }
        case 'PAUSE_SESSION': {
            if (!state.currentSession) return state;
            return {
                ...state,
                currentSession: { ...state.currentSession, status: 'paused' },
                targetEndTime: null, // Timer stops
            };
        }
        case 'RESUME_SESSION': {
            if (!state.currentSession) return state;
            // Calculate new target end time based on remaining time
            const targetEndTime = Date.now() + (state.timeRemaining * 1000);
            return {
                ...state,
                currentSession: { ...state.currentSession, status: 'active' },
                targetEndTime: targetEndTime,
            };
        }
        case 'END_SESSION': {
            if (!state.currentSession) return state;
            return {
                ...state,
                currentSession: {
                    ...state.currentSession,
                    status: 'completed',
                    endTime: new Date().toISOString(),
                },
                isFocusModeActive: false,
                timeRemaining: 0,
                targetEndTime: null,
            };
        }
        case 'TICK': {
            if (!state.currentSession || state.currentSession.status !== 'active' || !state.targetEndTime) return state;

            const now = Date.now();
            const secondsLeft = Math.max(0, Math.ceil((state.targetEndTime - now) / 1000));

            // Only update if changed to avoid renders
            if (secondsLeft === state.timeRemaining) return state;

            const finished = secondsLeft === 0;

            if (finished) {
                return {
                    ...state,
                    timeRemaining: 0,
                    targetEndTime: null,
                    currentSession: {
                        ...state.currentSession,
                        actualDurationSeconds: state.currentSession.duration * 60,
                    }
                };
            }

            return {
                ...state,
                timeRemaining: secondsLeft,
                currentSession: {
                    ...state.currentSession,
                    actualDurationSeconds: state.currentSession.actualDurationSeconds + 1,
                },
            };
        }
        case 'SET_DURATION':
            return {
                ...state,
                timerDuration: action.payload,
                timeRemaining: action.payload * 60,
                targetEndTime: null // Reset any potential target if we change duration while idle
            };
        case 'RESET_SESSION':
            return {
                ...initialState,
                timerDuration: state.timerDuration,
                timeRemaining: state.timerDuration * 60,
                targetEndTime: null,
            };
        case 'LOG_INTERRUPTION': {
            if (!state.currentSession) return state;
            return {
                ...state,
                currentSession: {
                    ...state.currentSession,
                    interruptionCount: state.currentSession.interruptionCount + 1,
                    interruptionLog: [
                        ...state.currentSession.interruptionLog,
                        { timestamp: new Date().toISOString(), reason: action.payload }
                    ]
                }
            };
        }
        default:
            return state;
    }
}

// --- Context ---
interface FocusContextType {
    state: FocusState;
    startSession: (taskId?: string, durationMinutes?: number) => void;
    pauseSession: () => void;
    resumeSession: () => void;
    endSession: () => void;
    setDuration: (minutes: number) => void;
    logInterruption: (reason?: string) => void;
    resetSession: () => void;
    formatTime: (seconds: number) => string;
}

const FocusContext = createContext<FocusContextType | undefined>(undefined);

export function FocusProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(focusReducer, initialState);

    // Persistence Effect
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    // Timer Effect
    useEffect(() => {
        let interval: NodeJS.Timeout;

        if (state.currentSession?.status === 'active' && state.targetEndTime) {
            // Ticking logic
            interval = setInterval(() => {
                const now = Date.now();
                if (state.targetEndTime && now >= state.targetEndTime) {
                    dispatch({ type: 'TICK' });
                } else {
                    dispatch({ type: 'TICK' });
                }
            }, 1000);

            // Initial check
            dispatch({ type: 'TICK' });

        } else if (state.timeRemaining === 0 && state.currentSession?.status === 'active' && !state.targetEndTime) {
            dispatch({ type: 'END_SESSION' });
            playWhiteNoise(false);
        }

        return () => clearInterval(interval);
    }, [state.currentSession?.status, state.targetEndTime]);

    // Firestore Sync Logic (Stub)
    useEffect(() => {
        if (state.currentSession?.status === 'completed') {
            saveSessionToFirestore(state.currentSession);
        }
    }, [state.currentSession?.status]);

    // Actions
    const startSession = (taskId?: string, durationMinutes: number = state.timerDuration) => {
        dispatch({ type: 'START_SESSION', payload: { taskId, durationMinutes } });
        playWhiteNoise(true);
    };

    const pauseSession = () => {
        dispatch({ type: 'PAUSE_SESSION' });
        playWhiteNoise(false);
    };

    const resumeSession = () => {
        dispatch({ type: 'RESUME_SESSION' });
        playWhiteNoise(true);
    };

    const endSession = () => {
        dispatch({ type: 'END_SESSION' });
        playWhiteNoise(false);
    };

    const setDuration = (minutes: number) => {
        dispatch({ type: 'SET_DURATION', payload: minutes });
    };

    const resetSession = () => {
        dispatch({ type: 'RESET_SESSION' });
        playWhiteNoise(false);
    };

    const logInterruption = (reason?: string) => {
        dispatch({ type: 'LOG_INTERRUPTION', payload: reason });
    };

    // Helper
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Stubs
    const playWhiteNoise = (params: boolean) => {
        // Implementation stub for Audio API
        // console.log(play ? '🎵 Starting White Noise...' : 'Silence.');
    };

    const saveSessionToFirestore = async (session: FocusSession) => {
        // Stub for firestoreService
        console.log('[FocusContext] Saving session to Firestore:', {
            collection: 'focus_sessions',
            payload: session
        });
    };

    return (
        <FocusContext.Provider value={{
            state,
            startSession,
            pauseSession,
            resumeSession,
            endSession,
            setDuration,
            logInterruption,
            resetSession,
            formatTime
        }}>
            {children}
        </FocusContext.Provider>
    );
}

export function useFocusContext() {
    const context = useContext(FocusContext);
    if (context === undefined) {
        throw new Error('useFocusContext must be used within a FocusProvider');
    }
    return context;
}
